<template>
  <card :title="title" id="details" v-if="canEdit || canDelete" v-cloak>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-3 text-left">
       <label class="control-label">{{ $t("scale.weighing_on_scale.contract_number") }}</label>
        <div class="sm-select-input-dialog-btn" @click="contractClick">
          <div class="sm-select-input-dialog-info">
            <div class="input_value">{{ getContractNames }}</div>
          </div>
          <div class="sm-select-input-dialog-actions">
            <span
              v-if="selectedContracts && selectedContracts.length > 0"
              class="sm-select-input-dialog-clear"
              @click.stop="clearSelectedContracts"
              :title="$t('common.delete')"
            >
              <i class="fa fa-times"></i>
            </span>
            <span class="sm-select-input-dialog-icon">
              <i class="fa-regular fa-edit"></i>
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-3 text-left">
       <label class="control-label">{{ $t("stock_management.orders.title") }}</label>
        <div class="sm-select-input-dialog-btn" @click="orderClick">
          <div class="sm-select-input-dialog-info">
            <div class="input_value">{{ selectedOrder ? selectedOrder.order_number : " - " }}</div>
          </div>
          <div class="sm-select-input-dialog-actions">
            <span
              v-if="selectedOrder"
              class="sm-select-input-dialog-clear"
              @click.stop="selectedOrder = null"
              :title="$t('common.delete')"
            >
              <i class="fa fa-times"></i>
            </span>
            <span class="sm-select-input-dialog-icon">
              <i class="fa-regular fa-edit"></i>
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-3 text-left">
        <label class="control-label">{{
          $t("common.product_name")
        }}</label>
        <autocomplete-search
          :placeholder="$t('stock_management.products.search_by_name')"
          :dispatch="getActiveProducts"
          search-key="name"
          ref="productName"
          :required="true"
          :data="productName"
          @onSelected="onProductNameSelected"
          @onChange="onProductNameChanged"
          :disabled="selectedContracts?.length > 0"
        ></autocomplete-search>
      </div>
      <div class="form-group col-sm-12 col-lg-3 text-left">
        <label class="control-label">{{
          $t("stock_management.products.code")
        }}</label>
        <autocomplete-search
          :placeholder="$t('stock_management.products.search_by_code')"
          :dispatch="getActiveProducts"
          search-key="code"
          ref="productCode"
          :class="{ 'field-error': $v.data.product.code.$error }"
          :required="true"
          :data="productCode"
          @onSelected="onProductCodeSelected"
          @onChange="onProductCodeChanged"
          :disabled="selectedContracts?.length > 0"
        ></autocomplete-search>
      </div>
      <div class="form-group col-sm-12 col-lg-3 text-left">
        <label class="control-label">{{
          $t("stock_management.products.barcode")
        }}</label>
        <autocomplete-search
          :placeholder="$t('stock_management.products.search_by_barcode')"
          :dispatch="getActiveProducts"
          search-key="barcode"
          ref="productBarcode"
          :required="true"
          :data="productBarcode"
          @onSelected="onProductBarcodeSelected"
          @onChange="onProductBarcodeChanged"
          :disabled="selectedContracts?.length > 0"
        ></autocomplete-search>
      </div>
    </div>

    <div class="row" v-if="!data.product.uuid && !!data.product.name">
      <div class="form-group col-sm-12 col-lg-3 text-left">
        <label class="control-label">{{
          $t("stock_management.products.type")
        }}</label>
        <b-form-select
          v-model="data.product.product_type_uuid"
          :class="{ 'field-error': $v.data.product.product_type_uuid.$error }"
          class="form-control"
          :options="product_type_list"
          :disabled="!!data.product.uuid"
        ></b-form-select>
      </div>
      <div class="col-sm-4 col-lg-3 form-group">
        <label class="control-label">{{
          $t("stock_management.products.select_mu")
        }}</label>
        <v-select
          v-model="data.product.measure_unit"
          :reduce="(option) => option"
          :class="{ 'field-error': $v.data.product.measure_unit_id.$error }"
          :placeholder="$t('stock_management.products.select_mu')"
          label="name"
          autocomplete="off"
          :options="measure_unit_list"
          @input="onMeasureUnitSelected"
          class="custom-select-dropdown-height"
          :disabled="!!data.product.uuid"
        ></v-select>
      </div>
    </div>

    <div class="row">
      <div class="form-group col-sm-12 col-lg-3 text-left">
        <label class="control-label">{{
          $t("stock_management.reception.date")
        }}</label>
        <date-picker2
          v-model="data.reception_date"
          :value-type="reception_date_input"
          :format="getDefaultDateFormat"
          type="date"
          :input-class="['date-picker-style']"
          :class="{ 'field-error': this.$v.data.reception_date.$error }"
          @change="onReceptionDateChanged"
          ref="reception_date"
          :disabled="(!!data.batch_uuid && !!data.reception_date)"
        ></date-picker2>
      </div>
      <div class="col-sm-4 col-lg-3 form-group">
        <label class="control-label">{{
          $t("common.partner")
        }}</label>
        <autocomplete-search
          :placeholder="$t('stock_management.partners.search')"
          :dispatch="getPartners"
          search-key="name"
          ref="partner"
          :required="true"
          :data="partnerName"
          @onSelected="onPartnerSelected"
          @onChange="onPartnerChanged"
          :disabled="selectedContracts?.length > 0"
        ></autocomplete-search>
        <img
          src="~@/assets/images/desktop/icons/controls/plus.svg"
          class="icon-svg float-right text-success c-pointer"
          @click="showPartnerDialog"
          v-if="addNewPartner === 'add'"
        />
        <img
          src="~@/assets/images/desktop/icons/controls/edit.svg"
          class="icon-svg float-right text-success c-pointer"
          @click="showPartnerDialog"
          v-if="addNewPartner === 'edit'"
        />
      </div>
    </div>

    <div class="row">
      <div class="form-group col-sm-12 col-lg-3 text-left">
        <label class="control-label">{{
          $t("stock_management.products.unit_price_net")
        }}</label>
        <input
          v-model="data.product.unit_price"
          class="form-control upper"
          :class="{ 'field-error': $v.data.product.unit_price.$error }"
          v-on:keydown="onlyPrice"
          placeholder="0.00"
        />
      </div>
      <div class="col-sm-12 col-lg-3 form-group">
        <label class="control-label">{{
          $t("stock_management.products.vat")
        }}</label>
        <b-form-select
          v-model="data.vat_code"
          class="form-control"
          :options="get_vat_code_list"
          label="name"
        ></b-form-select>
      </div>
    </div>

    <div class="row">
      <div class="form-group col-sm-12 col-lg-3 text-left">
        <label class="control-label">{{
          $t("common.quantity")
        }}</label>
        <DxNumberBox
          v-model="data.product.quantity"
          :min="0"
          :showSpinButtons="false"
          class="dx-form-control"
          :class="{ 'field-error': $v.data.product.quantity.$error }"
          :format="quantityFormatter"
          placeholder="0.00"
        />
      </div>
      <div class="col-sm-4 col-lg-3 form-group">
        <label class="control-label">{{ $t("common.warehouse") }}</label>
        <b-form-select
          v-model="data.warehouse_uuid"
          class="form-control"
          :class="{ 'field-error': $v.data.warehouse_uuid.$error }"
          :options="warehouse_list"
          :disabled="!!data.batch_uuid"
          @change="onWarehouseChanged"
        ></b-form-select>
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-3 text-left">
        <label class="control-label">{{ $t("stock_management.shipping_number") }}</label>
        <input
          v-model="data.shipping_note_number"
          class="form-control"
          :maxlength="185"
          :disabled="!!data.batch_uuid"
        ></input>
      </div>
      <div class="form-group col-sm-12 col-lg-5 text-left">
        <label class="control-label">{{ $t("common.notes") }}</label>
        <textarea
          v-model="data.notes"
          class="form-control textarea-2-lines"
          :maxlength="185"
        ></textarea>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-6">
        <label class="control-label font-weight-bold">{{
          $t("common.total")
        }}</label>
        <div>{{ total + " " + $currency.name }}</div>
      </div>
    </div>

    <agro-action-buttons
      v-if="!saveButtonPressed"
      :data="data"
      :can-edit="canEdit"
      :can-delete="canDelete"
      @onSave="save"
      @onSaveAndNew="saveAndNew"
      :save-and-new="true"
      @onDelete="confirmDelete"
      @onGotoList="gotoList"
    ></agro-action-buttons>

    <div class="row" v-if="!saveButtonPressed && canEdit">
      <div class="col-12 text-right pt-2">
        <button type="button" class="btn btn-primary" @click="saveAndSendErp">
          {{ $t("stock_management.reception.save_and_send_erp") }}
        </button>
      </div>
    </div>

    <div class="row mt-3" v-if="canEdit">
      <div class="col-12">
        <div class="erp-status-box">
          <div class="erp-status-head">
            <div>
              <strong>{{ $t("stock_management.reception.erp_integration_status") }}:</strong>
              <span
                class="erp-status-pill"
                :class="`erp-status-pill--${(erpQueueStatus || 'unknown').toLowerCase()}`"
              >
                {{ erpQueueStatusLabel }}
              </span>
            </div>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              :disabled="erpStatusLoading || erpStatusActionLoading"
              @click="refreshErpIntegrationStatus()"
            >
              {{ $t("stock_management.reception.erp_integration_refresh") }}
            </button>
          </div>

          <div v-if="erpQueueItem" class="erp-status-details">
            <div><strong>Queue UUID:</strong> {{ erpQueueItem.uuid }}</div>
            <div><strong>Source UUID:</strong> {{ erpQueueItem.source_uuid }}</div>
            <div v-if="erpQueueItem.last_error_message">
              <strong>{{ $t("stock_management.reception.erp_integration_last_error") }}:</strong>
              {{ erpQueueItem.last_error_message }}
            </div>
          </div>

          <div class="erp-status-actions">
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              :disabled="!canSendToErpNow"
              @click="sendToErpNow"
            >
              {{ $t("stock_management.reception.erp_integration_send_now") }}
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-warning"
              :disabled="!canRetryErpNow"
              @click="retryErpNow"
            >
              {{ $t("stock_management.reception.erp_integration_retry_now") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <CreatePartnerDialog
      :show="showPartnersModal"
      @close="showPartnersModal = false"
      :data="partnersModalData"
      @onOK="onPartnerDialogClosed"
    ></CreatePartnerDialog>

     <ContractsPurchaseSaleSelector
      v-if="showContractSelectDialog"
      ref="contractSelector"
      :visible="showContractSelectDialog"
      :selectedContractUuids="selectedContracts?.map((contract) => contract.uuid) ?? []"
      @close="showContractSelectDialog = false"
      @continue="onContinueFromContractSelector"
      :multiselect="false"
      :filterData="{
        contract_type_id: '1',
        show_completed_rows: 0,
        sale_type: 'partner',
      }"
    />

    <OrdersSelector
      v-if="showOrderSelectDialog"
      ref="orderSelector"
      :visible="showOrderSelectDialog"
      :selectedOrderUuids="selectedOrder ? [selectedOrder.uuid] : []"
      @close="showOrderSelectDialog = false"
      @continue="onContinueFromOrderSelector"
      :multiselect="false"
    />
  </card>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>
<script>
import { mapGetters, mapActions } from "vuex";
import {
  buildDefaultDropdownList,
  buildGroupedWarehouseDropdownList,
  only_price,
  errorMessage,
  successMessage,
} from "@/helpers/common";
import required from "vuelidate/src/validators/required";
import {
  canDelete,
  canEdit,
  permissions,
  permissionTypes,
} from "@/helpers/Permissions";
import * as actions from "@/helpers/generalActions";
import AgroActionButtons from "@/components/AgroActionButtons/index";
import Forbidden from "@/pages/errors/Forbidden";
import VueBootstrapTypeahead from "vue-bootstrap-typeahead";
import AutocompleteSearch from "~/components/Common/AutocompleteSearch/Index.vue";
import CreatePartnerDialog from "~/pages/stock-management/sm-partners/partials/CreatePartnerDialog.vue";
import DatePicker2 from "vue2-datepicker";
import moment from 'moment'
import DxNumberBox from "devextreme-vue/number-box";
import { getQuantityFormatter } from '~/helpers/stockHelper';
import ContractsPurchaseSaleSelector from "@/components/ContractsPurchaseSaleSelector.vue";
import OrdersSelector from "@/components/OrdersSelector.vue";

export default {
  name: "SMProductsDetails",
  components: {
    CreatePartnerDialog,
    AutocompleteSearch,
    Forbidden,
    AgroActionButtons,
    VueBootstrapTypeahead,
    DatePicker2,
    DxNumberBox,
    ContractsPurchaseSaleSelector,
    OrdersSelector,
  },
  data() {
    return {
      data: {
        product: {
          name: null 
        },
        reception_date: null,
        batch_uuid: null,
        shipping_note_number: null,
      },
      product_types: this.productTypes ? this.productTypes : [],
      addNewPartner: false,
      showPartnersModal: false,
      partnersModalData: {},
      productCode: null,
      productBarcode: null,
      productName: null,
      partnerName: null,
      reception_date_input: null,
      use_default_0_unit_price: false,
      saveButtonPressed: false,
      showContractSelectDialog: false,
      selectedContracts: [],
      erpQueueItem: null,
      erpLastSourceUuid: null,
      erpStatusLoading: false,
      erpStatusActionLoading: false,
      showOrderSelectDialog: false,
      selectedOrder: null,
      isOrderSelectionMandatory: false,
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      users: "user/users",
      domain: "auth/domain",
      products: "smProducts/smProducts",
      productTypes: "smProductTypes/smProductTypes",
      measureUnits: "measureUnits/measureUnits",
      warehouses: "smWarehouses/smWarehouses",
      partners: "smPartners/smPartners",
      getDefaultDateFormat: 'settings/getDefaultDateFormat',
      smProductVatList: "smProducts/smProductVatList",
    }),
    quantityFormatter(){
      return getQuantityFormatter();
    },
    name() {
      return this.$t("stock_management.reception.title");
    },
    config() {
      return {
        permission: permissions.SM_STOCK,
        permission_type: permissionTypes.module,
        get: this.get,
        create: this.create,
        update: this.update,
        delete: this.delete,
        list_url: "/stock-management/sm-stocks",
        confirm_delete: true,
      };
    },
    actions() {
      return actions;
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    title() {
      return this.name;
    },
    product_type_list() {
      return buildDefaultDropdownList(Object.assign(this.productTypes));
    },
    measure_unit_list() {
      if (this.measureUnits) {
        return this.measureUnits.map((item) => ({
          value: item.id,
          name: `${item.name} (${item.symbol})`,
        }));
      }
      return [];
    },
    warehouse_list() {
      return buildGroupedWarehouseDropdownList(Object.assign([], this.warehouses));
    },
    get_vat_code_list() {
      return buildDefaultDropdownList(Object.assign([], this.smProductVatList ?? []), { value: "id", text: "name" });
    },
    total() {
      if (this.data.product.quantity > 0 && this.data.product?.unit_price) {
        return (
          this.data.product.quantity * this.data.product.unit_price
        ).toFixed(2);
      }
      return (0).toFixed(2);
    },
    erpQueueStatus() {
      return this.erpQueueItem?.status || null;
    },
    erpQueueStatusLabel() {
      return this.erpQueueStatus || this.$t("stock_management.reception.erp_integration_status_not_found");
    },
    canSendToErpNow() {
      if (this.erpStatusLoading || this.erpStatusActionLoading || this.saveButtonPressed) {
        return false;
      }

      if (!this.erpQueueItem) {
        return !!this.erpLastSourceUuid;
      }

      return ["NEW", "VALIDATED", "MAPPED", "RETRY_WAITING"].includes(this.erpQueueItem.status);
    },
    canRetryErpNow() {
      if (!this.erpQueueItem || this.erpStatusLoading || this.erpStatusActionLoading || this.saveButtonPressed) {
        return false;
      }

      return ["ERROR", "DEAD"].includes(this.erpQueueItem.status);
    },
     getContractNames() {
      if (!this.selectedContracts || this.selectedContracts.length === 0) {
        return " - ";
      }
      return this.selectedContracts?.map((l) => l.contract_number).join(", ");
    },
    isUnitPriceReadOnly() {
      // Unit price is read-only if setting is enabled and an order is selected
      return this.isOrderSelectionMandatory && this.selectedOrder;
    },
  },
  methods: {
    ...mapActions({
      getProductTypes: "smProductTypes/searchSmProductTypes",
      get: "smProducts/getSmProduct",
      create: "smTransactions/createReceptionTransaction",
      update: "smStocks/updateSmStock",
      delete: "smStocks/deleteSmStock",
      getProductsRaw: "smProducts/getSmProducts",
      getMeasureUnits: "measureUnits/searchMeasureUnits",
      getWarehouses: "smWarehouses/getSmWarehouses",
      getPartners: "smPartners/getSmPartners",
      checkReceptionShippingNumberAlreadyExists: "smTransactions/checkReceptionShippingNumberAlreadyExists",
      sendReceptionTransactionToErp: "smTransactions/sendReceptionTransactionToErp",
      fetchReceptionQueue: "erpIntegration/fetchReceptionQueue",
      sendReceptionQueueItem: "erpIntegration/sendReceptionQueueItem",
      retryReceptionQueueItem: "erpIntegration/retryReceptionQueueItem",
      getGroupByCode: 'settings/getGroupByCode',
      getSmProductVatList: "smProducts/getSmProductVatList",
    }),
    getActiveProducts(searchCriteria = {}) {
      const payload = {
        ...searchCriteria,
        filter: [["is_active", "=", 1]],
      };
      return this.getProductsRaw(payload);
    },
    async refreshErpIntegrationStatus(sourceUuid = null) {
      const sourceToUse = sourceUuid || this.erpLastSourceUuid || null;
      const search = sourceToUse || this.data.shipping_note_number || null;

      if (!search) {
        this.erpQueueItem = null;
        return;
      }

      this.erpStatusLoading = true;
      try {
        const result = await this.fetchReceptionQueue({
          source_type: "SM_RECEPTION",
          search,
          limit: 25,
        });

        const list = Array.isArray(result?.data) ? result.data : [];
        const item = sourceToUse
          ? list.find((queueItem) => queueItem?.source_uuid === sourceToUse) || null
          : (list[0] || null);

        this.erpQueueItem = item;
        if (item?.source_uuid) {
          this.erpLastSourceUuid = item.source_uuid;
        }
      } finally {
        this.erpStatusLoading = false;
      }
    },
    async sendToErpNow() {
      this.erpStatusActionLoading = true;
      try {
        if (this.erpQueueItem?.uuid) {
          await this.sendReceptionQueueItem({ uuid: this.erpQueueItem.uuid, force: true });
        } else if (this.erpLastSourceUuid) {
          await this.sendReceptionTransactionToErp({
            source_type: "SM_RECEPTION",
            source_uuid: this.erpLastSourceUuid,
            source_document_number: this.data.shipping_note_number || null,
            source_event_at: this.data.reception_date || null,
            send_mode: "TRY_NOW",
          });
        }

        successMessage(this, this.$t("stock_management.reception.erp_integration_action_done"));
      } catch (e) {
        errorMessage(this, this.$t("stock_management.reception.erp_send_error"));
      } finally {
        await this.refreshErpIntegrationStatus(this.erpQueueItem?.source_uuid || this.erpLastSourceUuid);
        this.erpStatusActionLoading = false;
      }
    },
    async retryErpNow() {
      if (!this.erpQueueItem?.uuid) {
        return;
      }

      this.erpStatusActionLoading = true;
      try {
        await this.retryReceptionQueueItem({ uuid: this.erpQueueItem.uuid, mode: "IMMEDIATE" });
        successMessage(this, this.$t("stock_management.reception.erp_integration_action_done"));
      } catch (e) {
        errorMessage(this, this.$t("stock_management.reception.erp_send_error"));
      } finally {
        await this.refreshErpIntegrationStatus(this.erpQueueItem?.source_uuid || this.erpLastSourceUuid);
        this.erpStatusActionLoading = false;
      }
    },
    extractBackendErrorMessage(error) {
      // errors.error holds the real underlying reason (e.g. per-entity nomenclator mapping detail);
      // the top-level `message` is often a generic wrapper string, so only fall back to it when no detail exists
      const errorBag = error?.response?.data?.errors?.error;
      const entries = Array.isArray(errorBag) ? errorBag : errorBag ? [errorBag] : [];
      const detail = entries.flat().filter(Boolean).join(" | ");

      return (
        detail ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        ""
      );
    },
    getErpExportFailureDialogMessage(erpExportResponse, fallbackSourceUuid = null) {
      const failedStatuses = ["ERROR", "DEAD", "CANCELED"];
      const queueItemsCandidates = [
        erpExportResponse?.data?.queue_item,
        erpExportResponse?.data?.queue_items,
        erpExportResponse?.data?.items,
        erpExportResponse?.data?.data,
        erpExportResponse?.data,
        erpExportResponse?.queue_item,
        erpExportResponse?.queue_items,
        erpExportResponse?.items,
        erpExportResponse,
      ];

      const queueItems = queueItemsCandidates.reduce((list, candidate) => {
        if (Array.isArray(candidate)) {
          return list.concat(candidate);
        }

        if (
          candidate &&
          typeof candidate === "object" &&
          (candidate.status || candidate.last_error_code || candidate.last_error_message || candidate.source_uuid)
        ) {
          list.push(candidate);
        }

        return list;
      }, []);

      const failedItem = queueItems.find((item) => {
        const status = (item?.status || "").toUpperCase();
        return failedStatuses.includes(status) || !!item?.last_error_code || !!item?.last_error_message;
      });

      if (!failedItem) {
        return null;
      }

      // last_error_message is already a clean, itemized, user-facing text - show it as-is instead of wrapping it in a technical sentence
      if (failedItem?.last_error_message) {
        return failedItem.last_error_message;
      }

      const errorDetails = failedItem?.last_error_code || this.$t("stock_management.reception.erp_send_failure_unknown_reason");

      return this.$t("stock_management.reception.erp_send_failure_dialog_message", {
        source_uuid: failedItem?.source_uuid || fallbackSourceUuid || this.$t("unknown"),
        status: failedItem?.status || this.$t("unknown"),
        error: errorDetails,
      });
    },
    async showErpSendErrorDialog(message) {
      // the dialog plugin renders html, so turn plain-text line breaks into <br> for the itemized message
      const htmlMessage = String(message || "").replace(/\n/g, "<br>");
      await this.$dialog.alert(htmlMessage).then((dialog) => {
        dialog.close();
      });
    },
    loadData() {
      let vm = this;
      return this.actions.getData(
        this,
        (data) => {
          vm.$set(vm.data, "product", {
            product_type_uuid: null,
            vat_code: null,
            measure_unit_id: null,
          });
          vm.$set(vm.data, "warehouse_uuid", null);
          vm.$set(vm.data, "vat_code", null);
          vm.$set(vm.data, "measure_unit_id", null);
          vm.$set(vm.data, "product_type_uuid", null);
        },
        (data) => {}
      );
    },
    gotoList() {
      return actions.gotoList(this);
    },
    confirmDelete() {
      return this.actions.confirmDelete(this);
    },
    checkShippingNumberAlreadyExist(callback){
      if(this.data.shipping_note_number && this.data.shipping_note_number.length > 0)
      {
            this.checkReceptionShippingNumberAlreadyExists({
                shipping_note_number: this.data.shipping_note_number
            }).then(response => {
                    if (response.is_exists && response.batch_uuid != this.data.batch_uuid) {
                        this.$dialog.confirm(this.$t("stock_management.reception.shipping_number_exist"), {
                            okText: this.$t("common.yes"),
                            cancelText: this.$t("common.no")
                      }).then((dialog) => {
                            dialog.close();
                            const responseDate = new Date(response.transaction_date);
                            const receptionDate = new Date(this.data?.reception_date);
                        if (response.batch_uuid && response.batch_uuid.length > 0 &&
                            responseDate.toDateString() === receptionDate.toDateString() &&
                            response.partner_uuid == this.data?.product?.partner?.uuid &&
                            response.warehouse_uuid == this.data?.warehouse_uuid) {
                              this.data.batch_uuid = response.batch_uuid;
                        }
                        callback();
                    }).catch(() => {
                        // Handle cancel
                    });
                } else {
                    callback();
                }
           });
      } else {
        callback();
      }
    },
    prepareSave(callback) {
      // Check if order selection is mandatory
      if (this.isOrderSelectionMandatory && !this.selectedOrder) {
        errorMessage(this, this.$t("stock_management.orders.order_selection_mandatory"));
        this.saveButtonPressed = false;
        return;
      }

      if (!this.data.product.product_type_uuid) {
        errorMessage(this, this.$t("stock_management.products.type_required"));
        this.saveButtonPressed = false;
        return;
      }

      this.$v.data.$touch();

      let productNameValidate = this.$refs["productName"].validate();
      let partnerValidate = this.$refs["partner"].validate();
      this.data.product.total = this.total;

      let whs = this.warehouses.find(
        (w) => w.uuid === this.data.warehouse_uuid
      );

      if (whs) {
        this.data.department_uuid = whs.department_uuid;
      }

      if (productNameValidate && partnerValidate && !this.$v.data.$invalid) {
        this.checkShippingNumberAlreadyExist(() => {
          const reception_to_save = {
            batch_uuid: this.data.batch_uuid,
            department_uuid: this.data.department_uuid,
            reception_date: this.data.reception_date,
            shipping_note_number: this.data.shipping_note_number,
            warehouse_uuid: this.data.warehouse_uuid,
            partner: this.data.product.partner,
            products: [
              {
                uuid: this.data.product.uuid,
                name: this.data.product.name,
                code: this.data.product.code,
                barcode: this.data.product.barcode,
                unit_price: this.data.product.unit_price,
                quantity: this.data.product.quantity,
                measure_unit_id: this.data.product.measure_unit_id,
                product_type_uuid: this.data.product.product_type_uuid,
                vat_code: this.data.vat_code,
                notes: this.data.notes,
                contract_uuid: this.selectedContracts.length ?  this.selectedContracts[0].uuid : null,
                order_uuid: this.selectedOrder ? this.selectedOrder.uuid : null,
              },
            ],
          };

          callback(reception_to_save);
         });
      } else {
        this.saveButtonPressed = false;
        errorMessage(this, this.$t('fill_out_error'));
      }
    },
    save() {
      if (this.saveButtonPressed) {
        return;
      }
      
      this.saveButtonPressed = true;
      this.prepareSave((reception_to_save) => {
        this.actions.save(this, this.config.list_url, reception_to_save).then(response => {
          }).catch((e) => {
            this.saveButtonPressed = false;
          })
      });
    },
    saveAndNew() {
      if (this.saveButtonPressed) {
        return;
      }
      this.saveButtonPressed = true;
        this.prepareSave((reception_to_save) => {
          this.actions.saveAndNew(this, undefined, null, reception_to_save).then(response => {
            this.product = {
              name: null
            };

            this.$set(this.data, "product", this.product);
            this.$set(this, "productBarcode", null);
            this.$set(this, "productCode", null);
            this.$set(this, "productName", null);
            this.$set(this.data, "vat_code", null);
            this.$set(this.data, "measure_unit_id", null);
            this.$set(this.data, "product_type_uuid", null);
            this.$set(this.data, "quantity", null);
            this.$set(this.data, "quantity_mu", null);
            this.$set(this.data, "notes", null);

            if(response && response.data && response.data.length > 0){
              this.onPartnerSelected(response.data[0].product.partner);
              this.$set(this.data, "batch_uuid", response.data[0].batch_uuid);
              this.$refs['partner'].$refs['search'].$refs.input.disabled = true;
              this.erpLastSourceUuid = response.data[0].uuid || null;
              this.refreshErpIntegrationStatus(this.erpLastSourceUuid);
            }

            this.saveButtonPressed = false;
          }).catch((e) => {
            this.saveButtonPressed = false;
            console.log(e);
            errorMessage(this,'Error on saving data');
          })
        });
    },
    saveAndSendErp() {
      if (this.saveButtonPressed) {
        return;
      }

      this.saveButtonPressed = true;
      this.prepareSave(async (reception_to_save) => {
        try {
          const createResponse = await this.create(reception_to_save);
          const createdTransactions = Array.isArray(createResponse?.data) ? createResponse.data : [];

          if (createdTransactions.length === 0) {
            this.saveButtonPressed = false;
            errorMessage(this, this.$t("stock_management.reception.erp_send_error"));
            return;
          }

          for (const transaction of createdTransactions) {
            if (!transaction?.uuid) {
              continue;
            }

            const erpExportResponse = await this.sendReceptionTransactionToErp({
              source_type: "SM_RECEPTION",
              source_uuid: transaction.uuid,
              source_document_number: this.data.shipping_note_number || null,
              source_event_at: this.data.reception_date || null,
              send_mode: "TRY_NOW",
            });

            const failureDialogMessage = this.getErpExportFailureDialogMessage(erpExportResponse, transaction.uuid);
            if (failureDialogMessage) {
              this.saveButtonPressed = false;
              errorMessage(this, this.$t("stock_management.reception.erp_send_error"));
              await this.showErpSendErrorDialog(failureDialogMessage);
              return;
            }
          }

          successMessage(this, this.$t("stock_management.reception.erp_send_success"));

          setTimeout(() => {
            this.$router.push({
              path: this.config.list_url,
            }).catch(() => {
              window.location.href = this.config.list_url;
            });
          }, 300);
        } catch (e) {
          this.saveButtonPressed = false;
          errorMessage(this, this.$t("stock_management.reception.erp_send_error"));
          const backendMessage = this.extractBackendErrorMessage(e);
          await this.showErpSendErrorDialog(
            backendMessage
              ? this.$t("stock_management.reception.erp_send_exception_dialog_message", { error: backendMessage })
              : this.$t("stock_management.reception.erp_send_error")
          );
        }
      });
    },
    newObject() {
      return (window.location.href = this.config.list_url + "/reception");
    },
    onlyPrice(event) {
      return only_price(event);
    },
    onProductCodeSelected(data) {
      this.fillProductData(data);
    },
    onProductCodeChanged(data) {
      this.data.product.code = data?.name;
      this.data.product.uuid = data?.uuid;
    },
    onProductBarcodeSelected(data) {
      this.fillProductData(data);
    },
    onProductBarcodeChanged(data) {
      this.data.product.barcode = data?.name;
      this.data.product.uuid = data?.uuid;
    },
    onProductNameSelected(data) {
      this.fillProductData(data);
    },
    onProductNameChanged(data) {
      this.$set(this.data.product, 'name', data?.name);
      this.$set(this.data.product, 'uuid', data?.uuid);

      if (!data || !data.uuid) {
        this.$set(this.data.product, "barcode", null);
        this.$set(this.data.product, "code", null);
        this.$set(this, "productBarcode", null);
        this.$set(this, "productCode", null);
      }
    },
    getProduct(product_uuid) {
      return this.products.find((p) => p.uuid === product_uuid);
    },
    fillProductData(product, callback = undefined) {
      if (product && product.uuid) {
        let prod = this.getProduct(product.uuid);
        if (prod) {
          this.data.product = prod;
          if(this.use_default_0_unit_price){
            this.data.product.unit_price = 0;
          }
          this.productName = {
            uuid: product.uuid,
            name: product.name,
          };
          this.productBarcode = {
            uuid: product.uuid,
            name: product.barcode,
          };
          this.productCode = {
            uuid: product.uuid,
            name: product.code,
          };
          this.partnerName = prod?.partner;

          if (callback !== undefined) {
            callback(this.data);
          }
        }
      }
    },
    onPartnerSelected(data) {
      this.data.product.partner = data;
      this.addNewPartner = this.data.product.partner.uuid ?? "edit";
    },
    onPartnerChanged(data) {
      if (!this.data?.product?.partner) {
        this.data.product.partner = {};
      }

      if (data?.name !== this.data.product.partner?.name) {
        this.addNewPartner = "add";
        this.partnersModalData = data; //init with name
      }
    },
    showPartnerDialog() {
      this.showPartnersModal = true;

      if (this.addNewPartner === "add") {
      } else {
        if (this.partnersModalData.name !== this.data.product.partner?.name) {
          this.partnersModalData = { name: this.partnersModalData.name }; //if name has changed while editing
        } else {
          this.partnersModalData = this.data.product.partner;
        }
      }

      this.partnersModalData.action = this.addNewPartner;
    },
    onPartnerDialogClosed(data) {
      this.showPartnersModal = false;

      if (data && data.name) {
        this.addNewPartner = "edit";
        this.data.product.partner = data;
        this.partnerName = data;
      }
    },
    onReceptionDateChanged(date) {
    },
    onMeasureUnitSelected(data) {
      if (data) {
        this.data.product.measure_unit = {
          id: data.value,
          name: data.name,
        };
        this.data.product.measure_unit_id = data.value;
      } else {
        this.data.product.measure_unit = null;
        this.data.product.measure_unit_id = null;
      }
    },
    contractClick() {
      this.showContractSelectDialog = true; // Show the ContractSelector dialog
    },
    orderClick() {
      this.showOrderSelectDialog = true; // Show the OrdersSelector dialog
    },
    onContinueFromContractSelector(selectedContracts) {
      this.showContractSelectDialog = false; // Close the dialog
      this.selectedContracts = selectedContracts;
      if (selectedContracts.length > 0) {
        this.data.warehouse_uuid = null;
        this.data.product = {
          uuid: selectedContracts[0].product_uuid,
          name: selectedContracts[0].product_name,
          barcode: selectedContracts[0].product_barcode,
          code: selectedContracts[0].product_code,
          product_type_uuid: selectedContracts[0].product_type_uuid,
          measure_unit_id: selectedContracts[0].measure_unit_id,
          unit_price: parseFloat(selectedContracts[0].unit_price_discounted || selectedContracts[0].unit_price || 0),
          partner: {
            uuid: selectedContracts[0].partner_uuid,
            name: selectedContracts[0].partner_name,
          },
        };
        this.productName = {
            uuid: selectedContracts[0].product_uuid,
            name: selectedContracts[0].product_name,
          };
          this.productBarcode = {
            uuid: selectedContracts[0].product_uuid,
            name: selectedContracts[0].product_barcode,
          };
          this.productCode = {
            uuid: selectedContracts[0].product_uuid,
            name: selectedContracts[0].product_code,
          };
          this.partnerName = {
            uuid: selectedContracts[0].partner_uuid,
            name: selectedContracts[0].partner_name,
          };
      }
    },
    onContinueFromOrderSelector(selectedOrders) {
      this.showOrderSelectDialog = false; // Close the dialog
      if (selectedOrders.length > 0) {
        const order = selectedOrders[0];
        this.selectedOrder = order;
        
        // Auto-populate contract if order has a contract
        if (order.contract_uuid) {
          // Fetch the contract details and select it
          this.selectedContracts = [{
            uuid: order.contract_uuid,
            contract_number: order.contract_number,
            product_uuid: order.product_uuid,
            product_name: order.product_name,
            product_barcode: order.product_barcode,
            product_code: order.product_code,
            product_type_uuid: order.product_type_uuid,
            measure_unit_id: order.measure_unit_id,
            partner_uuid: order.partner_uuid,
            partner_name: order.partner_name,
            unit_price_discounted: parseFloat(order.unit_price_discounted || order.unit_price || 0),
          }];
        }
        
        this.data.warehouse_uuid = null;
        this.data.product = {
          uuid: order.product_uuid,
          name: order.product_name,
          barcode: order.product_barcode,
          code: order.product_code,
          product_type_uuid: order.product_type_uuid,
          measure_unit_id: order.measure_unit_id,
          // Use unit_price_discounted from order if available, otherwise unit_price
          unit_price: parseFloat(order.unit_price_discounted || order.unit_price || 0),
          partner: {
            uuid: order.partner_uuid,
            name: order.partner_name,
          },
        };
        this.productName = {
          uuid: order.product_uuid,
          name: order.product_name,
        };
        this.productBarcode = {
          uuid: order.product_uuid,
          name: order.product_barcode,
        };
        this.productCode = {
          uuid: order.product_uuid,
          name: order.product_code,
        };
        this.partnerName = {
          uuid: order.partner_uuid,
          name: order.partner_name,
        };
      }
    },
    clearSelectedContracts() {
      this.selectedContracts = [];
    },
    onWarehouseChanged(value) {
      if (this.selectedContracts.length > 0 && value) {
        const selectedWarehouse = this.warehouses.find((w) => w.uuid === value);
        if (selectedWarehouse) {
          if (
            this.selectedContracts[0].legal_entity_uuid &&
            this.selectedContracts[0].legal_entity_uuid !== selectedWarehouse.legal_entity_uuid
          ) {
            errorMessage(this,'Depozitul selectat nu aparține aceleiași entități juridice ca și contractul selectat!');
            this.data.warehouse_uuid = null;
            return;
          }
        }
      }
    }    
  },
  mounted() {
    this.data.reception_date = moment().format(this.getDefaultDateFormat);
    this.getProductTypes({
      pagination: 0,
    });
    this.loadData();
    this.getMeasureUnits({
      enablePagination: 0,
    });
    this.getWarehouses({
      enablePagination: 0,
      limit: 1000,
      filter: [["is_active", "=", 1]],
    });
    this.actions.handleParams(this);

    this.getGroupByCode({
      code: 'STOCK_MANAGEMENT'
    }).then(response => {
        let _setting = response.items.find(obj => obj.code == 'USE_DEFAULT_0_UNIT_PRICE_ON_RECEPTION');
        this.use_default_0_unit_price = false;
        if(_setting !== undefined){
          this.use_default_0_unit_price = _setting?.value == '1';
          if(this.use_default_0_unit_price){
            this.data.product.unit_price = 0;
          }
        }

        // Check GOODS_RECEIPT_WITH_PO_ONLY setting
        let goodsReceiptSetting = response.items.find(obj => obj.code == 'GOODS_RECEIPT_WITH_PO_ONLY');
        this.isOrderSelectionMandatory = goodsReceiptSetting?.value == '1' || goodsReceiptSetting?.value === true;
    });

    this.getSmProductVatList();
  },
  watch: {
    productTypes: {
      handler: function (newVal) {
        this.product_types = Object.keys(newVal).map((key) => {
          return newVal[key];
        });
      },
      deep: true,
    },
    $route: {
      handler: function (newVal) {
        this.actions.handleParams(this);
      },
      deep: true,
    },
    permission: {
      handler: function (newVal) {
        this.permissionModule = newVal;
      },
      deep: true,
    },
    data: {
      handler: (data) => {},
      deep: true,
    },
    "data.shipping_note_number": {
      handler() {
        this.refreshErpIntegrationStatus();
      },
    },
  },
  validations() {
    return {
      data: {
        product: {
          name: {
            required,
          },
          unit_price: {
            required,
          },
          quantity: {
            required,
          },
          measure_unit_id: {
            required,
          },
          product_type_uuid: {
            required,
          },
          code: {
            required: (value) => !!this.data.product.uuid || !!value,
          },
        },
        warehouse_uuid: {
          required,
        },
        reception_date: {
          required,
        },
      },
    };
  },
};
</script>

<style scoped>
@import "@/assets/scss/components/selectButtonStyle.scss";

.textarea-2-lines {
  height: calc(1.5em * 2 + 0.75em + 2px);
}

.a-icon {
  position: relative;
  top: -26px;
  left: +40px;
}

.float-right {
  float: right;
  position: relative;
  top: -40px;
  left: 30px;
}

.erp-status-box {
  border: 1px solid #dfe6ef;
  border-radius: 8px;
  background: #f8fbff;
  padding: 12px;
}

.erp-status-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.erp-status-pill {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  background: #e7edf5;
  color: #2f4762;
}

.erp-status-pill--confirmed {
  background: #dff4e8;
  color: #19703a;
}

.erp-status-pill--error,
.erp-status-pill--dead {
  background: #fde3e6;
  color: #9b1c31;
}

.erp-status-pill--new,
.erp-status-pill--validated,
.erp-status-pill--mapped,
.erp-status-pill--retry_waiting,
.erp-status-pill--sent {
  background: #e4efff;
  color: #1f4f8f;
}

.erp-status-details {
  margin-top: 8px;
  font-size: 13px;
  color: #2f4762;
  display: grid;
  gap: 4px;
}

.erp-status-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}
</style>
